import React, { useState } from 'react'
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Grid,
  CircularProgress,
  Button,
  Alert,
  Chip,
  Breadcrumbs,
  useTheme,
  alpha,
  Divider,
  useMediaQuery,
  Stack,
  TextField,
  IconButton,
  Tooltip,
  Card,
  CardActionArea,
  CardMedia,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { 
  Folder as FolderIcon, 
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon,
  Home as HomeIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  ImageSearch as ImageSearchIcon,
  Image as ImageIcon,
  PhotoLibrary as GalleryIcon,
  Info as InfoIcon,
  CheckCircleOutline as CheckIcon,
  Filter as FilterIcon,
  Sort as SortIcon
} from '@mui/icons-material'
import AssetLoader, { encodeFilePath } from '../../utils/AssetLoader'
import { uploadCustomModel } from '../../api/files'

// Wersja komponentu
const COMPONENT_VERSION = '1.2.0'

// Gallery Image component
const GalleryImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: 220,
  objectFit: 'cover',
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.03)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
  },
  '&.selected': {
    border: `3px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}))

// Gallery Card component
const GalleryCard = styled(Paper)(({ theme }) => ({
  width: '100%',
  height: 250,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
  },
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
}))

// Hidden file input
const HiddenInput = styled('input')({
  display: 'none',
})

// Garment types list
const garmentTypes = [
  { value: 'jacket', label: 'Kurtka', icon: '🧥' },
  { value: 'coat', label: 'Płaszcz', icon: '🧥' },
  { value: 'dress', label: 'Sukienka', icon: '👗' },
  { value: 'pants', label: 'Spodnie i Jeans', icon: '👖' },
  { value: 'shorts', label: 'Spodenki', icon: '🩳' },
  { value: 'sweater', label: 'Sweter', icon: '🧶' },
  { value: 'vest', label: 'Kamizelka', icon: '🦺' },
  { value: 't-shirt', label: 'Koszulka', icon: '👕' },
  { value: 'sweatshirt', label: 'Bluza', icon: '👚' },
  { value: 'dress-skirt', label: 'Sukienka i spódnica', icon: '👗' },
  { value: 'sweatshirt-sweater', label: 'Bluza i sweter', icon: '🧥' },
  { value: 'legging', label: 'Legginsy', icon: '👖' }
];

interface ModelSelectionProps {
  onSelectModel: (modelData: { 
    type: string, 
    image: string, 
    isCustom: boolean, 
    metadata?: {
      gender?: string,
      modelName?: string,
      pose?: string,
      garmentType?: string
    } 
  }) => void
  projectName: string
  username: string
  onSelect?: (model: string, imagePath: string, modelType: string) => void
  selectedModel?: string
}

const ModelSelection = ({ onSelectModel, projectName, username, onSelect, selectedModel: initialSelectedModel }: ModelSelectionProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [tabValue, setTabValue] = useState(0)
  const [modelType, setModelType] = useState<'man' | 'woman' | ''>('')
  const [directories, setDirectories] = useState<Array<{name: string, type: string}>>([])
  const [selectedDirectory, setSelectedDirectory] = useState<string>('')
  const [images, setImages] = useState<string[]>([])
  const [selectedImage, setSelectedImage] = useState<string>('')
  
  // for custom model upload
  const [uploading, setUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<{success: boolean, message: string} | null>(null)
  const [customModelPreview, setCustomModelPreview] = useState<string>('')
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  
  // For breadcrumb navigation
  const [navigationHistory, setNavigationHistory] = useState<Array<{level: number, path: string, name: string}>>([])
  const [currentLevel, setCurrentLevel] = useState(0)
  const [loading, setLoading] = useState(false)
  
  // For selecting garment type
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [selectedPose, setSelectedPose] = useState<string>('')
  const [selectedGarment, setSelectedGarment] = useState<string>('')
  const [selectedGarmentType, setSelectedGarmentType] = useState<string>('')
  
  // Extract the directory name from path safely, handling objects and strings
  const extractDirectoryName = (dir: any): string => {
    if (!dir) return '';
    if (typeof dir === 'string') {
      const parts = dir.split('/');
      return parts[parts.length - 1];
    }
    if (typeof dir === 'object') {
      return String(dir);
    }
    return String(dir);
  };
  
  // Handle image selection
  const handleImageSelect = (imageSrc: string) => {
    // Make sure imageSrc is a string
    const safeImageSrc = extractDirectoryName(imageSrc);
    setSelectedImage(safeImageSrc);
    
    // Log the original path for debugging
    console.log(`Selected image original path: ${safeImageSrc}`);
    
    // Handle spaces in image paths to avoid "file not found" errors
    // We use a custom function for URL encoding that preserves the path structure
    // but properly encodes spaces and special characters in filenames
    const encodedPath = encodeFilePath(safeImageSrc);
    console.log(`Selected image encoded path: ${encodedPath}`);
    
    // Create additional metadata about the selection - ensure all fields are strings
    const modelInfo = {
      gender: extractDirectoryName(modelType),
      modelName: extractDirectoryName(selectedModel),
      pose: extractDirectoryName(selectedPose),
      garmentType: extractDirectoryName(selectedGarmentType || selectedGarment)
    };
    
    console.log('Selected model complete info:', modelInfo);
    
    // Notify parent component with the encoded path and metadata
    if (onSelectModel) {
      onSelectModel({
        type: extractDirectoryName(modelType),
        image: encodedPath,
        isCustom: false,
        // Include additional metadata for future reference
        metadata: modelInfo
      });
    }
    
    // Call the onSelect prop if provided
    if (onSelect) {
      onSelect(extractDirectoryName(modelType), encodedPath, modelType);
    }
  }

  // Handle custom model file upload
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return
    
    const file = files[0]
    handleFile(file)
  }

  // Handle file dropped on drop zone
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0]
      handleFile(file)
    }
  }

  // Prevent default behavior for dragover
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  // Process the file - create preview and prepare for upload
  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setUploadStatus({
        success: false,
        message: 'Only image files are allowed'
      })
      return
    }
    
    // Create a preview URL
    const previewUrl = URL.createObjectURL(file)
    setCustomModelPreview(previewUrl)
    
    // Upload the file
    uploadFile(file)
  }

  // Upload the file to the server
  const uploadFile = async (file: File) => {
    setUploading(true)
    setUploadStatus(null)
    
    try {
      // Create form data
      const formData = new FormData()
      formData.append('file', file)
      formData.append('username', username)
      formData.append('project', projectName)
      
      // Upload the file
      const response = await fetch(`/api/upload_custom_model?username=${username}&project=${projectName}`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      })
      
      const result = await response.json();
      console.log('Upload result:', result);
      
      if (result.status === 'success' && result.path) {
        setUploadStatus({
          success: true,
          message: 'Model uploaded successfully'
        });
        
        // Clean up blob URL to avoid memory leaks
        if (customModelPreview && customModelPreview.startsWith('blob:')) {
          URL.revokeObjectURL(customModelPreview);
        }
        
        // Update preview to show server path instead of blob URL
        setCustomModelPreview(result.path);
        
        // Use the server-provided path for the image with metadata
        if (onSelectModel) {
          onSelectModel({
            type: 'custom',
            image: result.path,
            isCustom: true,
            metadata: {
              gender: 'custom',
              modelName: 'custom-upload',
              pose: 'custom',
              garmentType: selectedGarment || 'generic'
            }
          });
        
          // Also call onSelect if provided
          if (onSelect) {
            onSelect('custom', result.path, 'custom');
          }
        }
      } else {
        setUploadStatus({
          success: false,
          message: result.message || 'Upload failed'
        });
      }
    } catch (error) {
      console.error('Error uploading custom model:', error);
      setUploadStatus({
        success: false,
        message: error instanceof Error ? error.message : 'Error uploading file'
      });
    } finally {
      setLoading(false);
    }
  }

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    
    // Reset model selection when switching tabs
    if (newValue === 0) {
      // Gallery tab
      if (selectedImage) {
        const modelInfo = {
          gender: modelType || '',
          modelName: selectedModel || '',
          pose: selectedPose || '',
          garmentType: selectedGarmentType || selectedGarment || ''
        };
        
        if (onSelectModel) {
          onSelectModel({ 
            type: modelType || '', 
            image: selectedImage, 
            isCustom: false,
            metadata: modelInfo
          });
        }
        
        if (onSelect) {
          onSelect(modelType || '', selectedImage, modelType || '');
        }
      }
    } else {
      // Custom upload tab
      if (customModelPreview) {
        if (onSelectModel) {
          onSelectModel({
            type: 'custom',
            image: customModelPreview,
            isCustom: true,
            metadata: {
              gender: 'custom',
              modelName: 'custom',
              pose: '',
              garmentType: ''
            }
          });
          
          if (onSelect) {
            onSelect('custom', customModelPreview, 'custom');
          }
        }
      }
    }
  }

  // Navigate back to a specific level in the hierarchy
  // Level 0: Gender selection (man/woman)
  // Level 1: Model selection (John)
  // Level 2: Pose selection (front)
  // Level 3: Garment selection (shirt)
  const handleNavigateBack = (level: number) => {
    console.log(`Navigating back to level ${level}`);
    
    // If already at root level, do nothing
    if (level === 0 && currentLevel === 0) {
      return;
    }
    
    // Reset all selections after this level
    if (level <= 0) {
      setModelType('')
      setSelectedModel('')
      setSelectedPose('')
      setSelectedGarment('')
      setSelectedGarmentType('')
      setSelectedImage('')
      setCurrentLevel(0)
      loadInitialCategories()
      setNavigationHistory([])
      return
    }
    
    // Find the history entry for this level
    const historyEntry = navigationHistory.find(entry => entry.level === level)
    
    if (historyEntry) {
      // Reset everything after this level
      if (level <= 1) {
        setSelectedModel(historyEntry.name)
        setSelectedPose('')
        setSelectedGarment('')
        setSelectedGarmentType('')
        setSelectedImage('')
      } else if (level <= 2) {
        setSelectedPose(historyEntry.name)
        setSelectedGarment('')
        setSelectedGarmentType('')
        setSelectedImage('')
      } else if (level <= 3) {
        setSelectedGarment(historyEntry.name)
        setSelectedGarmentType('')
        setSelectedImage('')
      }
      
      // Update current level
      setCurrentLevel(level)
      
      // Update navigation history
      const newHistory = navigationHistory.filter(entry => entry.level <= level)
      setNavigationHistory(newHistory)
      
      // Load the appropriate directory based on the level
      if (level === 1) {
        // Load model directory
        loadDirectoryForModel(modelType, historyEntry.name)
      } else if (level === 2) {
        // Load pose directory
        loadDirectoryForPose(modelType, selectedModel, historyEntry.name)
      } else if (level === 3) {
        // Load garment directory
        loadDirectoryForGarment(modelType, selectedModel, selectedPose, historyEntry.name)
      }
    }
  }

  // Load initial categories (man/woman)
  const loadInitialCategories = async () => {
    setLoading(true)
    try {
      const path = 'models';
      const dirs = await AssetLoader.loadDirectories(path);
      
      // Convert to directory objects, ensuring they're always proper objects
      const dirObjects = dirs.map(dir => {
        // Check if dir is already a directory object
        if (typeof dir === 'object' && dir !== null) {
          // Make TypeScript happy by checking for properties properly
          const nameVal = dir && Object.prototype.hasOwnProperty.call(dir, 'name') ? String((dir as any).name) : '';
          const typeVal = dir && Object.prototype.hasOwnProperty.call(dir, 'type') ? String((dir as any).type) : 'directory';
          return {
            name: extractDirectoryName(nameVal),
            type: extractDirectoryName(typeVal)
          };
        }
        // If dir is a string, create a proper directory object
        return { name: extractDirectoryName(dir), type: 'directory' };
      });
      setDirectories(dirObjects)
      setLoading(false)
    } catch (error) {
      console.error('Error loading initial categories:', error)
      
      // Fallback directories if API fails
      setDirectories([
        { name: 'man', type: 'directory' },
        { name: 'woman', type: 'directory' }
      ])
      setLoading(false)
    }
  }

  // Handle selection of initial category (man/woman)
  const handleCategorySelect = async (category: string) => {
    const safeType = category.toLowerCase()
    setModelType(safeType as 'man' | 'woman')
    
    // Add to navigation history
    setNavigationHistory([
      { level: 1, path: `models/${safeType}`, name: safeType }
    ])
    
    setCurrentLevel(1)
    setSelectedDirectory(safeType)
    
    // Load models for this category
    loadDirectoryForModel(safeType)
  }

  // Load directory for model
  const loadDirectoryForModel = async (type: string, modelName?: string) => {
    setLoading(true)
    try {
      const safeType = type.toLowerCase()
      const path = `models/${safeType}`
      const dirs = await AssetLoader.loadDirectories(path)
      
      console.log(`Loaded directories for model ${safeType}:`, dirs)
      
      // Convert to directory objects with proper type checking
      const dirObjects = dirs.map(dir => {
        if (typeof dir === 'object' && dir !== null) {
          const hasName = 'name' in dir;
          const hasType = 'type' in dir;
          
          if (hasName && hasType) {
            return {
              name: extractDirectoryName(String((dir as any).name)),
              type: String((dir as any).type)
            };
          }
        }
        return { name: extractDirectoryName(String(dir)), type: 'directory' };
      });
      
      // Filter out non-directory entries (e.g., images)
      const filteredDirs = dirObjects.filter(dir => dir.type === 'directory');
      
      setDirectories(filteredDirs)
      setLoading(false)
      
      // Auto-select a model if specified
      if (modelName) {
        handleModelSelect(modelName)
      }
    } catch (error) {
      console.error(`Error loading directories for model ${type}:`, error)
      
      // Fallback based on type
      if (type === 'man') {
        setDirectories([
          { name: 'John', type: 'directory' }
        ]);
      } 
      // Fallback for woman
      else if (type === 'woman') {
        setDirectories([
          { name: 'Sarah', type: 'directory' }
        ]);
      }
      
      setLoading(false)
    }
  }

  // Handle model selection
  const handleModelSelect = async (modelName: string) => {
    setSelectedModel(modelName)
    
    // Add to navigation history - preserve previous entries for level 1
    const newHistory = [
      // Keep existing level 1 entry, if any
      ...navigationHistory.filter(entry => entry.level === 1),
      // Add or update level 2 entry
      { level: 2, path: `models/${modelType}/${modelName}`, name: modelName }
    ]
    
    setNavigationHistory(newHistory)
    setCurrentLevel(2)
    
    // Load poses for this model
    loadDirectoryForPose(modelType, modelName)
  }

  // Load directory for pose (front, back, etc.)
  const loadDirectoryForPose = async (type: string, modelName: string, poseName?: string) => {
    setLoading(true)
    try {
      const path = `models/${type}/${modelName}`
      const dirs = await AssetLoader.loadDirectories(path)
      
      console.log(`Loaded directories for pose in model ${modelName}:`, dirs)
      
      // Convert to directory objects with proper type checking
      const dirObjects = dirs.map(dir => {
        if (typeof dir === 'object' && dir !== null) {
          const hasName = 'name' in dir;
          const hasType = 'type' in dir;
          
          if (hasName && hasType) {
            return {
              name: extractDirectoryName(String((dir as any).name)),
              type: String((dir as any).type)
            };
          }
        }
        return { name: extractDirectoryName(String(dir)), type: 'directory' };
      });
      
      // Filter out non-directory entries
      const filteredDirs = dirObjects.filter(dir => dir.type === 'directory');
      
      setDirectories(filteredDirs)
      setLoading(false)
      
      // Auto-select a pose if specified
      if (poseName) {
        handlePoseSelect(poseName)
      }
    } catch (error) {
      console.error(`Error loading directories for pose in model ${modelName}:`, error)
      
      // Fallback poses
      setDirectories([
        { name: 'front', type: 'directory' },
        { name: 'back', type: 'directory' },
        { name: 'side', type: 'directory' }
      ])
      
      setLoading(false)
    }
  }

  // Handle pose selection
  const handlePoseSelect = async (poseName: string) => {
    setSelectedPose(poseName)
    
    // Add to navigation history
    const newHistory = [
      // Keep existing level 1-2 entries
      ...navigationHistory.filter(entry => entry.level <= 2),
      // Add or update level 3 entry  
      { level: 3, path: `models/${modelType}/${selectedModel}/${poseName}`, name: poseName }
    ]
    
    setNavigationHistory(newHistory)
    setCurrentLevel(3)
    
    // Load garments for this pose
    loadDirectoryForGarment(modelType, selectedModel, poseName)
  }

  // Load directory for garment (shirt, pants, etc.)
  const loadDirectoryForGarment = async (type: string, modelName: string, poseName: string, garmentName?: string) => {
    setLoading(true)
    try {
      const path = `models/${type}/${modelName}/${poseName}`
      const dirs = await AssetLoader.loadDirectories(path)
      
      console.log(`Loaded directories for garment in pose ${poseName}:`, dirs)
      
      // Convert to directory objects with proper type checking
      const dirObjects = dirs.map(dir => {
        if (typeof dir === 'object' && dir !== null) {
          const hasName = 'name' in dir;
          const hasType = 'type' in dir;
          
          if (hasName && hasType) {
            return {
              name: extractDirectoryName(String((dir as any).name)),
              type: String((dir as any).type)
            };
          }
        }
        return { name: extractDirectoryName(String(dir)), type: 'directory' };
      });
      
      // Filter out non-directory entries
      const filteredDirs = dirObjects.filter(dir => dir.type === 'directory');
      
      setDirectories(filteredDirs)
      setLoading(false)
      
      // Auto-select a garment if specified
      if (garmentName) {
        handleGarmentSelect(garmentName)
      }
    } catch (error) {
      console.error(`Error loading directories for garment in pose ${poseName}:`, error)
      
      // Fallback garments based on gender
      if (type === 'man') {
        setDirectories([
          { name: 'shirt', type: 'directory' },
          { name: 't-shirt', type: 'directory' },
          { name: 'jacket', type: 'directory' }
        ])
      } else {
        setDirectories([
          { name: 'dress', type: 'directory' },
          { name: 'blouse', type: 'directory' },
          { name: 'skirt', type: 'directory' }
        ])
      }
      
      setLoading(false)
    }
  }

  // Handle garment selection
  const handleGarmentSelect = async (garmentName: string) => {
    setSelectedGarment(garmentName)
    setSelectedGarmentType(garmentName) // Also set as garment type
    
    // Add to navigation history
    const newHistory = [
      // Keep existing level 1-3 entries
      ...navigationHistory.filter(entry => entry.level <= 3),
      // Add or update level 4 entry
      { level: 4, path: `models/${modelType}/${selectedModel}/${selectedPose}/${garmentName}`, name: garmentName }
    ]
    
    setNavigationHistory(newHistory)
    setCurrentLevel(4)
    
    // Load images for this garment
    loadImagesForGarment(modelType, selectedModel, selectedPose, garmentName)
  }

  // Load images for garment
  const loadImagesForGarment = async (type: string, modelName: string, poseName: string, garmentName: string) => {
    setLoading(true)
    try {
      const path = `models/${type}/${modelName}/${poseName}/${garmentName}`
      const files = await AssetLoader.loadImages(path)
      
      console.log(`Loaded images for garment ${garmentName}:`, files)
      
      setImages(files)
      setLoading(false)
    } catch (error) {
      console.error(`Error loading images for garment ${garmentName}:`, error)
      
      // Fallback - empty images
      setImages([])
      setLoading(false)
    }
  }

  // Initialize component
  React.useEffect(() => {
    loadInitialCategories()
  }, [])

  // Get translated label for a key
  const getLabel = (key: string) => {
    const translations: Record<string, string> = {
      'man': 'Mężczyzna',
      'woman': 'Kobieta',
      'front': 'Przód',
      'back': 'Tył',
      'side': 'Bok',
      'shirt': 'Koszula',
      't-shirt': 'T-shirt',
      'jacket': 'Kurtka',
      'dress': 'Sukienka',
      'blouse': 'Bluzka',
      'skirt': 'Spódnica',
      'custom': 'Własne zdjęcie',
      'gallery': 'Galeria',
      'browseLibrary': 'Przeglądaj modele',
      'uploadCustom': 'Wgraj własne zdjęcie',
      'selectCategory': 'Wybierz kategorię',
      'selectModel': 'Wybierz model',
      'selectPose': 'Wybierz pozę',
      'selectGarment': 'Wybierz rodzaj odzieży',
      'selectImage': 'Wybierz obraz',
      'uploadImage': 'Wgraj własne zdjęcie',
      'dragAndDropHere': 'Przeciągnij i upuść zdjęcie tutaj',
      'or': 'lub',
      'browseFiles': 'Przeglądaj pliki',
      'uploading': 'Wgrywanie...',
      'uploadSuccess': 'Wgrano pomyślnie!',
      'uploadError': 'Błąd wgrywania',
      'forBestResults': 'Dla najlepszych rezultatów:',
      'tip1': 'Make sure you are clearly visible, standing in a neutral pose',
      'tip2': 'Wear simple, solid-colored clothing',
      'tip3': 'Use good lighting with a neutral background',
      'dragAndDrop': 'Drag and drop your photo here or click to browse',
      'selectPhoto': 'Select Photo',
      'uploadBtn': 'Upload Photo',
      'categoryDescription': 'Directories only, PNG files shown as thumbnails on the right',
    };
    
    return translations[key] || key;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{ mb: 2 }}
      >
        <Tab 
          icon={<GalleryIcon />} 
          label={getLabel('gallery')} 
          sx={{ 
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            minWidth: isMobile ? '100px' : '140px'
          }}
        />
        <Tab 
          icon={<UploadIcon />} 
          label={getLabel('custom')} 
          sx={{ 
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            minWidth: isMobile ? '100px' : '140px'
          }}
        />
      </Tabs>
      
      {/* Gallery Tab */}
      <Box role="tabpanel" hidden={tabValue !== 0}>
        {tabValue === 0 && (
          <Box sx={{ pt: 1 }}>
            {/* Breadcrumb Navigation */}
            <Breadcrumbs 
              separator="›" 
              sx={{ 
                mb: 2,
                '& .MuiBreadcrumbs-separator': {
                  color: theme.palette.text.secondary,
                  mx: 0.5
                }
              }}
            >
              <Chip
                label={<Box sx={{ display: 'flex', alignItems: 'center' }}><HomeIcon fontSize="small" sx={{ mr: 0.5 }} /> Home</Box>}
                onClick={() => handleNavigateBack(0)}
                sx={{ 
                  cursor: 'pointer',
                  bgcolor: currentLevel === 0 ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1)
                  }
                }}
              />
              
              {navigationHistory.map((item, index) => (
                <Chip
                  key={`nav-${item.level}-${index}`}
                  label={getLabel(item.name)}
                  onClick={() => handleNavigateBack(item.level)}
                  sx={{ 
                    cursor: 'pointer',
                    bgcolor: currentLevel === item.level ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.1)
                    }
                  }}
                />
              ))}
            </Breadcrumbs>
            
            {/* Directory/File Browser */}
            {currentLevel === 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {getLabel('selectCategory')}
                </Typography>
                <Grid container spacing={2}>
                  {directories.map((dir, index) => (
                    <Grid item xs={6} sm={4} md={3} key={`dir-${index}`}>
                      <Paper
                        elevation={3}
                        sx={{
                          p: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: theme.shadows[6]
                          }
                        }}
                        onClick={() => handleCategorySelect(dir.name)}
                      >
                        <Box 
                          sx={{ 
                            width: 60, 
                            height: 60, 
                            borderRadius: '50%',
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: 1
                          }}
                        >
                          {dir.name === 'man' ? '👨' : '👩'}
                        </Box>
                        <Typography variant="body1">
                          {getLabel(dir.name)}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
            
            {/* Model Selection Level */}
            {currentLevel === 1 && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {getLabel('selectModel')}
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {directories.map((dir, index) => (
                      <Grid item xs={6} sm={4} md={3} key={`model-${index}`}>
                        <GalleryCard
                          onClick={() => handleModelSelect(dir.name)}
                        >
                          <Box 
                            sx={{ 
                              width: 80, 
                              height: 80, 
                              borderRadius: '50%',
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              mb: 1
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                          </Box>
                          <Typography variant="body1" sx={{ mt: 1 }}>
                            {dir.name}
                          </Typography>
                        </GalleryCard>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
            
            {/* Pose Selection Level */}
            {currentLevel === 2 && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {getLabel('selectPose')}
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {directories.map((dir, index) => (
                      <Grid item xs={6} sm={4} md={3} key={`pose-${index}`}>
                        <GalleryCard
                          onClick={() => handlePoseSelect(dir.name)}
                        >
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                            {dir.name === 'front' ? '↑' : dir.name === 'back' ? '↓' : '↔️'}
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 1 }}>
                            {getLabel(dir.name)}
                          </Typography>
                        </GalleryCard>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
            
            {/* Garment Selection Level */}
            {currentLevel === 3 && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {getLabel('selectGarment')}
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {directories.map((dir, index) => (
                      <Grid item xs={6} sm={4} md={3} key={`garment-${index}`}>
                        <GalleryCard
                          onClick={() => handleGarmentSelect(dir.name)}
                        >
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                            {garmentTypes.find(g => g.value === dir.name)?.icon || '👕'}
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 1 }}>
                            {getLabel(dir.name)}
                          </Typography>
                        </GalleryCard>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
            
            {/* Image Selection Level */}
            {currentLevel === 4 && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {getLabel('selectImage')}
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {images.length === 0 ? (
                      <Grid item xs={12}>
                        <Alert severity="info">
                          No images found for this category
                        </Alert>
                      </Grid>
                    ) : (
                      images.map((image, index) => (
                        <Grid item xs={12} sm={6} md={4} key={`image-${index}`}>
                          <Card 
                            sx={{ 
                              position: 'relative',
                              border: selectedImage === image ? `2px solid ${theme.palette.primary.main}` : 'none'
                            }}
                          >
                            <CardActionArea onClick={() => handleImageSelect(image)}>
                              <CardMedia
                                component="img"
                                height="220"
                                image={image}
                                alt={`Image ${index + 1}`}
                                sx={{ 
                                  objectFit: 'cover',
                                  borderRadius: theme.shape.borderRadius
                                }}
                              />
                              <Box sx={{ 
                                position: 'absolute', 
                                bottom: 0, 
                                width: '100%', 
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                p: 1
                              }}>
                                <Typography variant="body2">
                                  {extractDirectoryName(image)}
                                </Typography>
                              </Box>
                              {selectedImage === image && (
                                <Box sx={{ 
                                  position: 'absolute', 
                                  top: 10, 
                                  right: 10,
                                  bgcolor: theme.palette.primary.main,
                                  borderRadius: '50%',
                                  p: 0.5
                                }}>
                                  <CheckIcon sx={{ color: 'white' }} />
                                </Box>
                              )}
                            </CardActionArea>
                          </Card>
                        </Grid>
                      ))
                    )}
                  </Grid>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
      
      {/* Custom Upload Tab */}
      <Box role="tabpanel" hidden={tabValue !== 1}>
        {tabValue === 1 && (
          <Box sx={{ pt: 1 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {getLabel('forBestResults')}
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>{getLabel('tip1')}</li>
                <li>{getLabel('tip2')}</li>
                <li>{getLabel('tip3')}</li>
              </Typography>
            </Paper>
            
            {/* Upload Area */}
            <Paper
              sx={{
                p: 4,
                border: '2px dashed',
                borderColor: theme.palette.divider,
                bgcolor: alpha(theme.palette.background.default, 0.6),
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minHeight: 300,
                position: 'relative',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.light, 0.05)
                }
              }}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              {customModelPreview ? (
                <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                  <img 
                    src={customModelPreview} 
                    alt="Custom model preview" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 400,
                      objectFit: 'contain',
                      borderRadius: 8
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{ 
                      position: 'absolute',
                      top: -16,
                      right: -16,
                      bgcolor: theme.palette.error.main,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.palette.error.dark
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setCustomModelPreview('')
                      if (fileInputRef.current) fileInputRef.current.value = ''
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <Box sx={{ mb: 2 }}>
                    <UploadIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      {getLabel('dragAndDrop')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {getLabel('or')}
                    </Typography>
                    <Button variant="contained" sx={{ mt: 2 }}>
                      {getLabel('selectPhoto')}
                    </Button>
                  </Box>
                </>
              )}
              
              <HiddenInput
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              
              {/* Upload status */}
              {uploading && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography>{getLabel('uploading')}</Typography>
                </Box>
              )}
              
              {uploadStatus && (
                <Alert 
                  severity={uploadStatus.success ? 'success' : 'error'}
                  sx={{ mt: 2, width: '100%' }}
                >
                  {uploadStatus.message}
                </Alert>
              )}
            </Paper>
            
            {customModelPreview && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => {}}
                  disabled={uploading}
                  startIcon={<UploadIcon />}
                >
                  {getLabel('uploadBtn')}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ModelSelection